import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CreditCard, 
  Building, 
  X, 
  Lock, 
  Check,
  AlertCircle,
  Loader2
} from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';

const PaymentModal = ({ isOpen, onClose, plan, billingCycle, onSuccess }) => {
  const [selectedMethod, setSelectedMethod] = useState('credit_card');
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [cardDetails, setCardDetails] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardName: ''
  });

  useEffect(() => {
    if (isOpen) {
      fetchPaymentMethods();
    }
  }, [isOpen]);

  const fetchPaymentMethods = async () => {
    try {
      const response = await axios.get('/api/payment/methods');
      setPaymentMethods(response.data);
    } catch (error) {
      console.error('Error fetching payment methods:', error);
    }
  };

  const handlePayment = async () => {
    if (!plan) return;

    setIsProcessing(true);
    try {
      const response = await axios.post('/api/payment/create', {
        plan_id: plan.id,
        billing_cycle: billingCycle,
        payment_method: selectedMethod
      });

      const { payment_url, transaction_id } = response.data;
      
      // Simulated payment process
      setTimeout(() => {
        // Simulate successful payment
        verifyPayment(transaction_id, 'success');
      }, 2000);

    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Ödeme işlemi başlatılamadı');
      setIsProcessing(false);
    }
  };

  const verifyPayment = async (transactionId, status) => {
    try {
      const response = await axios.post('/api/payment/verify', {
        transaction_id: transactionId,
        status: status
      });

      if (status === 'success') {
        toast.success('Ödeme başarıyla tamamlandı!');
        onSuccess();
        onClose();
      } else {
        toast.error('Ödeme başarısız oldu');
      }
    } catch (error) {
      console.error('Payment verification error:', error);
      toast.error('Ödeme doğrulanamadı');
    } finally {
      setIsProcessing(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(price);
  };

  const getMethodIcon = (methodId) => {
    switch (methodId) {
      case 'credit_card':
        return <CreditCard className="w-6 h-6" />;
      case 'bank_transfer':
        return <Building className="w-6 h-6" />;
      default:
        return <CreditCard className="w-6 h-6" />;
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-gray-800 rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
        >
          {/* Header */}
          <div className="p-6 border-b border-gray-700">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-white">Ödeme</h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <p className="text-gray-400 mt-2">
              {plan?.name} Plan - {billingCycle === 'yearly' ? 'Yıllık' : 'Aylık'}
            </p>
          </div>

          {/* Plan Summary */}
          <div className="p-6 border-b border-gray-700">
            <div className="bg-gray-700 rounded-lg p-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-white font-semibold">{plan?.name} Plan</span>
                <span className="text-2xl font-bold text-white">
                  {formatPrice(billingCycle === 'yearly' ? plan?.yearly_price : plan?.monthly_price)}
                </span>
              </div>
              <div className="text-sm text-gray-400">
                {billingCycle === 'yearly' ? 'Yıllık ödeme' : 'Aylık ödeme'}
                {billingCycle === 'yearly' && (
                  <span className="text-green-400 ml-2">
                    (Aylık {formatPrice(plan?.yearly_price / 12)} tasarruf!)
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Payment Methods */}
          <div className="p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Ödeme Yöntemi</h3>
            <div className="space-y-3">
              {paymentMethods.map((method) => (
                <button
                  key={method.id}
                  onClick={() => setSelectedMethod(method.id)}
                  disabled={!method.available}
                  className={`w-full p-4 rounded-lg border-2 transition-all ${
                    selectedMethod === method.id
                      ? 'border-blue-500 bg-blue-500/10'
                      : method.available
                      ? 'border-gray-600 hover:border-gray-500'
                      : 'border-gray-700 bg-gray-800/50 cursor-not-allowed opacity-50'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className="text-gray-400">
                      {getMethodIcon(method.id)}
                    </div>
                    <div className="flex-1 text-left">
                      <div className="text-white font-medium">{method.name}</div>
                      <div className="text-sm text-gray-400">{method.description}</div>
                    </div>
                    {selectedMethod === method.id && (
                      <Check className="w-5 h-5 text-blue-500" />
                    )}
                  </div>
                </button>
              ))}
            </div>

            {/* Card Details Form */}
            {selectedMethod === 'credit_card' && (
              <div className="mt-6 space-y-4">
                <h4 className="text-white font-semibold">Kart Bilgileri</h4>
                <div className="space-y-3">
                  <input
                    type="text"
                    placeholder="Kart Numarası"
                    value={cardDetails.cardNumber}
                    onChange={(e) => setCardDetails({...cardDetails, cardNumber: e.target.value})}
                    className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
                    maxLength="19"
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="text"
                      placeholder="MM/YY"
                      value={cardDetails.expiryDate}
                      onChange={(e) => setCardDetails({...cardDetails, expiryDate: e.target.value})}
                      className="bg-gray-700 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
                      maxLength="5"
                    />
                    <input
                      type="text"
                      placeholder="CVV"
                      value={cardDetails.cvv}
                      onChange={(e) => setCardDetails({...cardDetails, cvv: e.target.value})}
                      className="bg-gray-700 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
                      maxLength="4"
                    />
                  </div>
                  <input
                    type="text"
                    placeholder="Kart Sahibinin Adı"
                    value={cardDetails.cardName}
                    onChange={(e) => setCardDetails({...cardDetails, cardName: e.target.value})}
                    className="w-full bg-gray-700 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
                  />
                </div>
              </div>
            )}

            {/* Security Notice */}
            <div className="mt-6 flex items-center space-x-2 text-sm text-gray-400">
              <Lock className="w-4 h-4" />
              <span>Ödeme bilgileriniz SSL ile korunmaktadır</span>
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-gray-700">
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-3 text-gray-300 border border-gray-600 rounded-lg hover:bg-gray-700 transition-colors"
              >
                İptal
              </button>
              <button
                onClick={handlePayment}
                disabled={isProcessing}
                className="flex-1 px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    İşleniyor...
                  </>
                ) : (
                  `₺${billingCycle === 'yearly' ? plan?.yearly_price : plan?.monthly_price} Öde`
                )}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default PaymentModal;
